import OrderOptimizerListingRouteQueryParams from "../../../common/routes/types/order-optimizer-listing-route-params";
import OrderOptimizerRailyListingFilter, {
  OrderOptimizerRailyListingIgnoreCargoFilter,
  OrderOptimizerRailyListingIgnoreDriverRoutesFilter,
  OrderOptimizerRailyListingIgnoreOnlyWithCargoFilter,
  OrderOptimizerRailyListingIgnoreOnlyWithTaxiFilter,
  OrderOptimizerRailyListingIgnoreTaxiFilter,
  OrderOptimizerRailyListingMaximumDelayFilter,
  OrderOptimizerRailyListingMaximumHaltingTimeFilter,
  OrderOptimizerRailyListingOrderHumanIdFilter,
  OrderOptimizerRailyListingStatusFilter,
} from "../common/types/order-optimizer-raily-listing-filter";
import OrderOptimizerRailyListingFilterType from "../common/types/order-optimizer-raily-listing-filter-type";
import orderOptimizerRailyListingFiltersHelper from "./order-optimizer-raily-listing-filters.helper";

const getPage = (
  routeQueryFilterParams: OrderOptimizerListingRouteQueryParams,
): number | undefined => {
  return routeQueryFilterParams.page
    ? Number(routeQueryFilterParams.page)
    : undefined;
};

const getPageSize = (
  routeQueryFilterParams: OrderOptimizerListingRouteQueryParams,
): number | undefined => {
  return routeQueryFilterParams.pageSize
    ? Number(routeQueryFilterParams.pageSize)
    : undefined;
};

const getFilters = (
  routeQueryFilterParams: OrderOptimizerListingRouteQueryParams,
): OrderOptimizerRailyListingFilter[] => {
  const filters: OrderOptimizerRailyListingFilter[] = [];

  if (routeQueryFilterParams.orderHumanId) {
    const orderHumanIdFilter: OrderOptimizerRailyListingOrderHumanIdFilter = {
      type: OrderOptimizerRailyListingFilterType.ORDER_HUMAN_ID,
      value: Number(routeQueryFilterParams.orderHumanId),
    };

    filters.push(orderHumanIdFilter);
  }

  if (routeQueryFilterParams.maximumDelay) {
    const maximumDelayFilter: OrderOptimizerRailyListingMaximumDelayFilter = {
      type: OrderOptimizerRailyListingFilterType.MAXIMUM_DELAY,
      value: Number(routeQueryFilterParams.maximumDelay),
    };

    filters.push(maximumDelayFilter);
  }

  if (routeQueryFilterParams.maximumHaltingTime) {
    const maximumHaltingTimeFilter: OrderOptimizerRailyListingMaximumHaltingTimeFilter =
      {
        type: OrderOptimizerRailyListingFilterType.MAXIMUM_HALTING_TIME,
        value: Number(routeQueryFilterParams.maximumHaltingTime),
      };

    filters.push(maximumHaltingTimeFilter);
  }

  if (routeQueryFilterParams.ignoreDriverRoutes) {
    const ignoreDriverRoutesFilter: OrderOptimizerRailyListingIgnoreDriverRoutesFilter =
      {
        type: OrderOptimizerRailyListingFilterType.IGNORE_DRIVER_ROUTES,
        value: routeQueryFilterParams.ignoreDriverRoutes,
      };

    filters.push(ignoreDriverRoutesFilter);
  }

  if (routeQueryFilterParams.ignoreTaxi) {
    for (let taxi of routeQueryFilterParams.ignoreTaxi.split(",")) {
      const ignoreTaxiFilter: OrderOptimizerRailyListingIgnoreTaxiFilter = {
        type: OrderOptimizerRailyListingFilterType.IGNORE_TAXI,
        value: taxi,
      };

      filters.push(ignoreTaxiFilter);
    }
  }

  if (routeQueryFilterParams.ignoreOnlyWithTaxi) {
    for (let taxi of routeQueryFilterParams.ignoreOnlyWithTaxi.split(",")) {
      const ignoreOnlyWithTaxiFilter: OrderOptimizerRailyListingIgnoreOnlyWithTaxiFilter =
        {
          type: OrderOptimizerRailyListingFilterType.IGNORE_ONLY_WITH_TAXI,
          value: taxi,
        };

      filters.push(ignoreOnlyWithTaxiFilter);
    }
  }

  if (routeQueryFilterParams.ignoreCargo) {
    for (let cargo of routeQueryFilterParams.ignoreCargo.split(",")) {
      const ignoreCargoFilter: OrderOptimizerRailyListingIgnoreCargoFilter = {
        type: OrderOptimizerRailyListingFilterType.IGNORE_CARGO,
        value: cargo,
      };

      filters.push(ignoreCargoFilter);
    }
  }

  if (routeQueryFilterParams.ignoreOnlyWithCargo) {
    for (let cargo of routeQueryFilterParams.ignoreOnlyWithCargo.split(",")) {
      const ignoreOnlyWithCargoFilter: OrderOptimizerRailyListingIgnoreOnlyWithCargoFilter =
        {
          type: OrderOptimizerRailyListingFilterType.IGNORE_ONLY_WITH_CARGO,
          value: cargo,
        };

      filters.push(ignoreOnlyWithCargoFilter);
    }
  }

  if (routeQueryFilterParams.status) {
    const value =
      orderOptimizerRailyListingFiltersHelper().getStatusValueFromRouteQueryParam(
        routeQueryFilterParams.status,
      );

    if (value) {
      const statusFilter: OrderOptimizerRailyListingStatusFilter = {
        type: OrderOptimizerRailyListingFilterType.STATUS,
        value,
      };

      filters.push(statusFilter);
    }
  }

  return filters;
};

const createRouteQueryParams = (
  filters: OrderOptimizerRailyListingFilter[],
  page: number,
  pageSize: number,
): OrderOptimizerListingRouteQueryParams => {
  const orderHumanId = filters.find(
    (filter) =>
      filter.type === OrderOptimizerRailyListingFilterType.ORDER_HUMAN_ID,
  )?.value as OrderOptimizerRailyListingOrderHumanIdFilter["value"] | undefined;

  const maximumDelay = filters.find(
    (filter) =>
      filter.type === OrderOptimizerRailyListingFilterType.MAXIMUM_DELAY,
  )?.value as OrderOptimizerRailyListingMaximumDelayFilter["value"] | undefined;

  const maximumHaltingTime = filters.find(
    (filter) =>
      filter.type === OrderOptimizerRailyListingFilterType.MAXIMUM_HALTING_TIME,
  )?.value as
    | OrderOptimizerRailyListingMaximumHaltingTimeFilter["value"]
    | undefined;

  const ignoreDriverRoutes = filters.find(
    (filter) =>
      filter.type === OrderOptimizerRailyListingFilterType.IGNORE_DRIVER_ROUTES,
  )?.value as
    | OrderOptimizerRailyListingIgnoreDriverRoutesFilter["value"]
    | undefined;

  const ignoreTaxi = filters
    .filter(
      (filter) =>
        filter.type === OrderOptimizerRailyListingFilterType.IGNORE_TAXI,
    )
    .map((filter) => filter.value)
    .join(",");

  const ignoreOnlyWithTaxi = filters
    .filter(
      (filter) =>
        filter.type ===
        OrderOptimizerRailyListingFilterType.IGNORE_ONLY_WITH_TAXI,
    )
    .map((filter) => filter.value)
    .join(",");

  const ignoreCargo = filters
    .filter(
      (filter) =>
        filter.type === OrderOptimizerRailyListingFilterType.IGNORE_CARGO,
    )
    .map((filter) => filter.value)
    .join(",");

  const ignoreOnlyWithCargo = filters
    .filter(
      (filter) =>
        filter.type ===
        OrderOptimizerRailyListingFilterType.IGNORE_ONLY_WITH_CARGO,
    )
    .map((filter) => filter.value)
    .join(",");

  const status = filters.find(
    (filter) => filter.type === OrderOptimizerRailyListingFilterType.STATUS,
  )?.value as OrderOptimizerRailyListingStatusFilter["value"] | undefined;

  const routeQueryParams: OrderOptimizerListingRouteQueryParams = {
    page: page ? page.toString() : undefined,
    pageSize: pageSize ? pageSize.toString() : undefined,
    orderHumanId: orderHumanId ? orderHumanId.toString() : undefined,
    maximumDelay: maximumDelay ? maximumDelay.toString() : undefined,
    maximumHaltingTime: maximumHaltingTime
      ? maximumHaltingTime.toString()
      : undefined,
    ignoreDriverRoutes: ignoreDriverRoutes,
    ignoreTaxi: ignoreTaxi || undefined,
    ignoreOnlyWithTaxi: ignoreOnlyWithTaxi || undefined,
    ignoreCargo: ignoreCargo || undefined,
    ignoreOnlyWithCargo: ignoreOnlyWithCargo || undefined,
    status: status
      ? orderOptimizerRailyListingFiltersHelper().getStatusRouteQueryParamFromValue(
          status,
        )
      : undefined,
  };

  return routeQueryParams;
};
const orderOptimizerRailyListingFiltersService = {
  getPage,
  getPageSize,
  getFilters,
  createRouteQueryParams,
};

export default orderOptimizerRailyListingFiltersService;
